import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { Button, Card, Field, Page, TextInput } from '../components/ui.jsx';

/**
 * Rent setup — one screen, one submit.
 *
 * The user's actual mental model is: "I own this property. This room
 * has that tenant. They pay ₹X on the Yth of every month." Making
 * that a five-page click chain is what pushed them off.
 *
 * Here it's a single form. On submit we:
 *   1. Create the property
 *   2. Create the unit under it
 *   3. Create the tenant
 *   4. Start an active lease
 *   5. Generate this month's rent invoice
 * Then jump to the tenant's page so they see the pending rent
 * with a Record button ready.
 */

interface Property { id: string }
interface Unit { id: string }
interface Tenant { id: string }
interface Lease { id: string }

export function SetupPage() {
  const nav = useNavigate();
  const qc = useQueryClient();

  // Property
  const [propertyName, setPropertyName] = useState('');
  const [address, setAddress] = useState('');
  // Unit
  const [unitKind, setUnitKind] = useState<'flat' | 'room' | 'shop' | 'other'>('room');
  const [unitLabel, setUnitLabel] = useState('');
  // Tenant
  const [tenantName, setTenantName] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  // Rent
  const [rent, setRent] = useState('');
  const [deposit, setDeposit] = useState('');
  const [rentDueDay, setRentDueDay] = useState('5');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = useMutation({
    mutationFn: async () => {
      const rentPaise = Money.parse(rent).toPaise();
      const depositPaise = deposit ? Money.parse(deposit).toPaise() : 0;
      const flatNo = unitKind === 'flat' ? unitLabel : undefined;
      const roomNo = unitKind !== 'flat' ? unitLabel : undefined;
      const now = new Date();

      const { property } = await api<{ property: Property }>('/api/v1/properties', {
        method: 'POST',
        body: { name: propertyName, address: address || undefined },
      });

      const { unit } = await api<{ unit: Unit }>(`/api/v1/properties/${property.id}/units`, {
        method: 'POST',
        body: {
          kind: unitKind,
          flatNo, roomNo,
          monthlyRent: rentPaise,
          securityDeposit: depositPaise,
        },
      });

      const { tenant } = await api<{ tenant: Tenant }>('/api/v1/tenants', {
        method: 'POST',
        body: { fullName: tenantName, phone: tenantPhone || undefined },
      });

      const { lease } = await api<{ lease: Lease }>('/api/v1/leases', {
        method: 'POST',
        body: {
          unitId: unit.id, tenantId: tenant.id,
          startDate,
          monthlyRent: rentPaise,
          securityDeposit: depositPaise,
          rentDueDay: Number(rentDueDay),
        },
      });

      await api('/api/v1/rent/generate', {
        method: 'POST',
        body: { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 },
      });

      return { tenant, lease };
    },
    onSuccess: (res) => {
      // Refresh anything the dashboard/rent pages might have cached.
      qc.invalidateQueries();
      nav(`/tenants/${res.tenant.id}`, { replace: true });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Setup failed'),
    onSettled: () => setBusy(false),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    create.mutate();
  }

  return (
    <Page
      title="Set up rent tracking"
      subtitle="One property, one unit, one tenant — start collecting rent in a minute."
    >
      <form onSubmit={onSubmit} className="space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            1. Your building
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Property name" hint="e.g. 'Green Villa' or 'Sector 12 house'">
              <TextInput required value={propertyName} onChange={(e) => setPropertyName(e.target.value)} />
            </Field>
            <Field label="Address" hint="Optional">
              <TextInput value={address} onChange={(e) => setAddress(e.target.value)} />
            </Field>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            2. The room / flat
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Kind">
              <select
                value={unitKind}
                onChange={(e) => setUnitKind(e.target.value as typeof unitKind)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
              >
                <option value="room">Room</option>
                <option value="flat">Flat</option>
                <option value="shop">Shop</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label={unitKind === 'flat' ? 'Flat number' : 'Room label'} hint="e.g. '101' or 'Ground floor left'">
              <TextInput value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} />
            </Field>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            3. Your tenant
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Full name">
              <TextInput required value={tenantName} onChange={(e) => setTenantName(e.target.value)} />
            </Field>
            <Field label="Phone">
              <TextInput value={tenantPhone} onChange={(e) => setTenantPhone(e.target.value)} placeholder="+91 …" />
            </Field>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            4. The rent
          </h2>
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Monthly rent (₹)">
              <TextInput required value={rent} onChange={(e) => setRent(e.target.value)} placeholder="15000" />
            </Field>
            <Field label="Security deposit (₹)" hint="Optional">
              <TextInput value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="30000" />
            </Field>
            <Field label="Rent due day" hint="Day of month, 1–28">
              <TextInput type="number" min={1} max={28} value={rentDueDay} onChange={(e) => setRentDueDay(e.target.value)} />
            </Field>
            <Field label="Lease start date">
              <TextInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
          </div>
        </Card>

        <div className="flex items-center justify-end gap-2">
          {error && <span className="text-sm text-red-600">{error}</span>}
          <Button variant="ghost" type="button" onClick={() => nav('/')}>Cancel</Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Setting up…' : 'Save and start tracking'}
          </Button>
        </div>
      </form>
    </Page>
  );
}
